/**
 * Shared chat/constructor command parser.
 * Returns { handled, serverText, pendingCommand, localReply, metasid }
 *   handled     — true if the command was recognised
 *   serverText  — text to send to backend (null for purely local commands)
 *   pendingCommand — original command string to track the response
 *   localReply  — immediate reply text for the user (null if none)
 *   metasid     — extracted metasid for /create (null otherwise)
 */

/**
 * Реестр команд чата (PROP-384) — ОДИН список на разбор, справку и подсказку после «/».
 *
 * До этого список жил дважды: в `parseCommand` (что исполняется) и в `HELP_TEXT` (что
 * рассказывают человеку). Они уже разошлись — `/mcp_tools` описан в справке, но разбирается
 * не здесь, а на сервере. Третья копия ради выпадающего списка гарантированно разъехалась бы
 * с обеими: подсказка, предлагающая несуществующую команду, хуже отсутствующей подсказки.
 *
 * `where` — кто исполняет: `client` разбирается здесь, `server` уходит платформе как есть.
 * Для человека разницы нет, но она объясняет, почему часть команд не находится в этом файле.
 */
export const CHAT_COMMANDS = [
  {
    cmd: '/obj', args: 'имя', hint: 'найти и выбрать объект по имени', where: 'client',
  },
  {
    cmd: '/create', args: 'тип имя', hint: 'создать объект: table, catalog, cube, form, report, procedure, import, join', where: 'client',
  },
  {
    cmd: '/attrs', args: '', hint: 'показать атрибуты текущего объекта', where: 'client',
  },
  {
    cmd: '/sel', args: 'N', hint: 'выбрать элемент из списка по номеру', where: 'client',
  },
  {
    cmd: '/add attr', args: 'имя', hint: 'добавить атрибут в текущий объект', where: 'client',
  },
  {
    cmd: '/add_attr', args: 'object_id entity_id', hint: 'добавить атрибут по идентификаторам', where: 'client',
  },
  {
    cmd: '/export', args: '', hint: 'выгрузить структуру выбранных объектов в JSON', where: 'client',
  },
  {
    cmd: '/datatypes', args: '', hint: 'список типов данных', where: 'client',
  },
  {
    cmd: '/mcp_tools', args: '', hint: 'список MCP-инструментов', where: 'server',
  },
  {
    cmd: '/clear', args: '', hint: 'очистить контекст разговора', where: 'client',
  },
  {
    cmd: '/help', args: '', hint: 'список команд', where: 'client',
  },
  // Команды платформы: разбираются сервером (ChatManager.chatCommand), поэтому в parseCommand
  // их нет — и подсказка обязана их знать, иначе человек считает, что их не существует.
  {
    cmd: '/шаги', args: 'вкл|выкл', hint: 'показывать ли ход работы агента', where: 'server',
  },
  {
    cmd: '/движок', args: '', hint: 'кто сейчас отвечает и как это переключить', where: 'server',
  },
]

/** Команды, подходящие под начатый ввод. Пустая строка и не-команда дают пустой список. */
export function matchCommands(text) {
  const typed = String(text || '').trimStart()
  if (!typed.startsWith('/')) return []
  const head = typed.split(/\s+/)[0].toLowerCase()
  // Пока набрано только «/», показываем всё: человек нажал слэш именно затем, чтобы увидеть,
  // что вообще бывает.
  if (head === '/') return CHAT_COMMANDS
  return CHAT_COMMANDS.filter(c => c.cmd.startsWith(head) || head.startsWith(c.cmd))
}

export function parseCommand(text) {
  const cmd = text.trim().toLowerCase()

  // PROP-388: «помощь» без слэша больше не команда. Команда — то, что начинается со слэша;
  // слово, работающее как команда «иногда», и есть та самая догадка по формулировке, которую
  // мы убираем.
  if (cmd === '/help') {
    return {
      handled: true, serverText: null, pendingCommand: null, localReply: 'help', metasid: null,
    }
  }

  if (cmd === '/clear') {
    return {
      handled: true, serverText: null, pendingCommand: null, localReply: 'clear', metasid: null,
    }
  }

  if (cmd.startsWith('/create ')) {
    const args = text.trim().substring('/create '.length).trim()
    const spaceIdx = args.indexOf(' ')
    if (spaceIdx < 0) {
      return {
        handled: true,
        serverText: null,
        pendingCommand: null,
        localReply: 'Укажите тип и имя объекта. Например: `/create table Заказы`\nДоступные типы: `table`, `catalog`, `cube`, `form`, `report`, `procedure`, `import`',
        metasid: null,
      }
    }
    const metasid = args.substring(0, spaceIdx).trim().toLowerCase()
    const rawName = args.substring(spaceIdx + 1).trim()
    if (!rawName) {
      return {
        handled: true,
        serverText: null,
        pendingCommand: null,
        localReply: `Укажите имя объекта. Например: \`/create ${metasid} Заказы\``,
        metasid: null,
      }
    }
    const objectName = rawName.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    return {
      handled: true,
      serverText: `создай_объект ${metasid} ${objectName}`,
      pendingCommand: `/create ${metasid} ${objectName}`,
      localReply: null,
      metasid,
    }
  }

  if (cmd.startsWith('/obj ')) {
    const objectName = text.trim().substring(5).trim()
    if (!objectName) {
      return {
        handled: true, serverText: null, pendingCommand: null, localReply: 'Укажите имя объекта. Например: `/obj Пользователи`', metasid: null,
      }
    }
    return {
      handled: true, serverText: `открой ${objectName}`, pendingCommand: `/obj ${objectName}`, localReply: null, metasid: null,
    }
  }

  if (cmd.startsWith('/sel ')) {
    const num = text.trim().substring(5).trim()
    if (!num || !/^\d+$/.test(num)) {
      return {
        handled: true, serverText: null, pendingCommand: null, localReply: 'Укажите номер элемента. Например: `/sel 1`', metasid: null,
      }
    }
    return {
      handled: true, serverText: `выбери ${num}`, pendingCommand: `/sel ${num}`, localReply: null, metasid: null,
    }
  }

  if (cmd.startsWith('/add_attr ')) {
    const args = text.trim().substring('/add_attr '.length).trim().split(/\s+/)
    if (args.length < 2) {
      return {
        handled: true, serverText: null, pendingCommand: null, localReply: 'Использование: `/add_attr <object_id> <entity_id>`', metasid: null,
      }
    }
    return {
      handled: true, serverText: text.trim(), pendingCommand: text.trim(), localReply: null, metasid: null,
    }
  }

  if (cmd.startsWith('/add attr ')) {
    const attrName = text.trim().substring('/add attr '.length).trim()
    if (!attrName) {
      return {
        handled: true, serverText: null, pendingCommand: null, localReply: 'Укажите имя атрибута. Например: `/add attr Название`', metasid: null,
      }
    }
    return {
      handled: true, serverText: `добавь атрибут ${attrName}`, pendingCommand: `/add attr ${attrName}`, localReply: null, metasid: null,
    }
  }

  if (cmd === '/attrs') {
    return {
      handled: true, serverText: 'покажи атрибуты', pendingCommand: '/attrs', localReply: null, metasid: null,
    }
  }

  if (cmd === '/datatypes') {
    return {
      handled: true, serverText: '/datatypes', pendingCommand: '/datatypes', localReply: null, metasid: null,
    }
  }

  if (cmd === '/export') {
    return {
      handled: true, serverText: null, pendingCommand: null, localReply: 'export', metasid: null,
    }
  }

  return {
    handled: false, serverText: null, pendingCommand: null, localReply: null, metasid: null,
  }
}

export const HELP_TEXT = ['**Доступные команды:**']
  .concat(CHAT_COMMANDS.map(c => `- **${c.cmd}${c.args ? ` ${c.args}` : ''}** — ${c.hint}`))
  .join('\n')

/**
 * PROP-418: где стоит каретка в резиновом поле ввода — на первой строке / на последней.
 *
 * Нужно ровно для стрелок: в однострочном поле «вверх» ничего не значило и потому целиком
 * досталось истории команд (PROP-395). В многострочном оно прежде всего двигает каретку, и
 * без этой проверки первое же нажатие внутри набранного текста затирало бы его прошлой
 * командой. Правило общее для панели и страницы чата — потому и живёт здесь, а не в двух
 * компонентах порознь.
 */
export function atFirstLine(e) {
  const el = e.target
  if (!el || typeof el.selectionStart !== 'number') return true
  return !String(el.value).slice(0, el.selectionStart).includes('\n')
}

export function atLastLine(e) {
  const el = e.target
  if (!el || typeof el.selectionEnd !== 'number') return true
  return !String(el.value).slice(el.selectionEnd).includes('\n')
}
